import { useEffect, useRef, useState, useCallback } from "react";
import config from "../environments/config.js";

function DappierAnswerPageLoader({ nonce } = {}) {
  const env = config.services.env;
  const assetsUrl = env === 'production' 
      ? 'https://assets.dappier.com'
      : `https://assets-${env}.dappier.com`;

  // Configuration constants for fetch behavior
  const FETCH_TIMEOUT_MS = 10000; // 10 seconds
  const MAX_RETRY_ATTEMPTS = 3;
  const INITIAL_RETRY_DELAY_MS = 1000; // 1 second

  // Helper function to discover CSP nonce from multiple sources
  const getCSPNonce = useCallback(() => {
    // 1. Use nonce from props if provided
    if (nonce && typeof nonce === 'string' && nonce.trim()) {
      return nonce.trim();
    }

    // 2. Check for nonce in meta tag
    const metaNonce = document.querySelector('meta[name="csp-nonce"]')?.content;
    if (metaNonce && typeof metaNonce === 'string' && metaNonce.trim()) {
      return metaNonce.trim();
    }

    // 3. Check for nonce in window global variable
    if (typeof window !== 'undefined' && 
        window.__CSP_NONCE__ && 
        typeof window.__CSP_NONCE__ === 'string' && 
        window.__CSP_NONCE__.trim()) {
      return window.__CSP_NONCE__.trim();
    }

    // 4. No nonce found
    return null;
  }, [nonce]);

  // Ref to ensure the widget is loaded only once
  const widgetLoadedRef = useRef(false);
  const [widgetId, setWidgetId] = useState(null);
  const [error, setError] = useState(null);

  // Function to extract subdomain from current URL
  const getSubdomain = () => {
    return window.location.hostname;
  };

  // Effect to fetch widget ID on component mount
  useEffect(() => {
    // Helper function to create a delay for retry backoff
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Helper function to determine if an error should be retried
    const shouldRetry = (error, response) => {
      // Don't retry on 4xx client errors (except 408 Request Timeout and 429 Too Many Requests)
      if (response && response.status >= 400 && response.status < 500) {
        return response.status === 408 || response.status === 429;
      }
      
      // Retry on network errors, timeouts, and 5xx server errors
      return true;
    };

    // Function to fetch widget external ID from API with timeout and retry logic
    const fetchWidgetExternalId = async (subdomain) => {
      const apiUrl = env === 'production' 
        ? 'https://api.dappier.com'
        : `https://api.${env}.dappier.com`;
      
      let lastError;
      
      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        let abortController = new AbortController();
        let timeoutId;
        
        try {
          // Set up timeout
          timeoutId = setTimeout(() => {
            abortController.abort();
          }, FETCH_TIMEOUT_MS);
          
          const response = await fetch(`${apiUrl}/app/widget/external-id/${subdomain}`, {
            signal: abortController.signal
          });
          
          // Clear timeout on successful response
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            const error = new Error(`HTTP error! status: ${response.status}`);
            error.response = response;
            
            // Check if we should retry this error
            if (attempt < MAX_RETRY_ATTEMPTS && shouldRetry(error, response)) {
              lastError = error;
              console.warn(`Attempt ${attempt} failed with status ${response.status}, retrying...`);
              
              // Calculate exponential backoff delay
              const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
              await delay(delayMs);
              continue;
            }
            
            throw error;
          }
          
          const data = await response.json();
          
          if (data.external_ids && data.external_ids.length > 0) {
            return data.external_ids[0]; // Use the first entry
          } else {
            throw new Error('No external IDs found in response');
          }
          
        } catch (error) {
          // Clear timeout in case of error
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          
          // Handle AbortError (timeout)
          let processedError = error;
          if (error.name === 'AbortError') {
            processedError = new Error(`Request timeout after ${FETCH_TIMEOUT_MS}ms`);
          }
          
          lastError = processedError;
          
          // Check if we should retry this error
          if (attempt < MAX_RETRY_ATTEMPTS && shouldRetry(error, error.response)) {
            console.warn(`Attempt ${attempt} failed: ${error.message}, retrying...`);
            
            // Calculate exponential backoff delay
            const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
            await delay(delayMs);
            continue;
          }
          
          // If this is the last attempt or error shouldn't be retried, throw the error
          break;
        } finally {
          // Clean up AbortController
          abortController = null;
        }
      }
      
      // Surface the final error to the caller
      console.error('Failed to fetch widget external ID after all retry attempts:', lastError);
      throw lastError;
    };

    const initializeWidget = async () => {
      try {
        const subdomain = getSubdomain();
        
        if (!subdomain) {
          setError('No subdomain found in URL');
          return;
        }

        const fetchedWidgetId = await fetchWidgetExternalId(subdomain);
        setWidgetId(fetchedWidgetId);
      } catch (error) {
        setError(error.message);
      }
    };

    initializeWidget();
  }, [env]);

  // Effect to load the widget once we have the widget ID
  useEffect(() => {
    if (!widgetId || widgetLoadedRef.current) {
      return;
    }

    // Variable to store reference to the specific widget element created by this component
    let widgetRef = null;

    // Get CSP nonce for script execution
    const cspNonce = getCSPNonce();

    // Create the loader script if it hasn't been created already
    const loaderScript = document.createElement('script');
    loaderScript.src = `${assetsUrl}/widget/dappier-loader.min.js`;
    loaderScript.async = true;

    // Set CSP nonce if available
    if (cspNonce) {
      loaderScript.setAttribute('nonce', cspNonce);
    }

    // Set attributes for environment and widget ID
    loaderScript.setAttribute('widget-id', widgetId);
    loaderScript.setAttribute('version', '2.0.0');
    loaderScript.setAttribute('variant', 'answer_page');

    if(env !== 'production') {
      loaderScript.setAttribute('env', env);
    }

    // Define behavior when the script loads
    loaderScript.onload = () => {
      console.log("Loader script loaded successfully.");

      // Create the widget element with required attributes
      const widgetElement = document.createElement('dappier-ask-ai-widget');
      widgetElement.setAttribute('widget-id', widgetId);
      widgetElement.setAttribute('variant', 'answer_page');

      // Store reference to this specific widget element
      widgetRef = widgetElement;

      // Replace React app cleanly: answer_page should take over the full page
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.innerHTML = ''; // Clear React app
        rootElement.appendChild(widgetElement); // Widget takes over #root
      } else {
        // Fallback to body if #root not found
        document.body.appendChild(widgetElement);
      }

      // Set the ref to true to prevent multiple loads
      widgetLoadedRef.current = true;
    };

    // Error handling for the script load failure
    loaderScript.onerror = () => {
      console.error("Failed to load the Dappier widget loader script.");
      setError("Failed to load the Dappier widget loader script.");
    };

    // Append the loader script to the document
    document.body.appendChild(loaderScript);

    // Cleanup: remove the script and widget when component unmounts
    return () => {
      if (document.body.contains(loaderScript)) {
        document.body.removeChild(loaderScript);
      }
      
      // Remove the specific widget element created/managed by this component
      if (widgetRef) {
        // Check if it's still in the DOM and remove it
        if (widgetRef.parentNode) {
          console.log("Cleaning up answer_page widget from", widgetRef.parentNode.tagName || 'unknown container');
          widgetRef.remove(); // Modern way to remove element
        }
        
        // Also clean up any other answer_page widgets that might be lingering
        const orphanedWidgets = document.querySelectorAll(`dappier-ask-ai-widget[widget-id="${widgetId}"][variant="answer_page"]`);
        orphanedWidgets.forEach(widget => {
          if (widget !== widgetRef) { // Don't double-remove our main widget
            console.log("Cleaning up orphaned answer_page widget");
            widget.remove();
          }
        });
      }
    };
  }, [assetsUrl, widgetId, env, getCSPNonce]);

  // Render error state if there's an error
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h2>Error loading Answer Page</h2>
        <p>{error}</p>
      </div>
    );
  }

  // Render loading state while fetching widget ID
  if (!widgetId) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  return null;
}

export default DappierAnswerPageLoader;